import { IPublicClientApplication } from "@azure/msal-browser";
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { ConfirmationDialogContextType } from "../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import PublicClientApplicationHolder from "../../PublicClientApplicationHolder";
import { tesaDarkRedError } from "../../styles/colors";
import AuthError from "../../models/AuthError";

interface ApiConfig {
  baseUrl?: string;
  useAccessToken?: boolean;
}

interface AuthTokens {
  idToken: string;
  accessToken: string;
}

class Api {
  constructor(private dialogContext: ConfirmationDialogContextType, config?: ApiConfig) {
    const { baseUrl, useAccessToken } = config || {};
    this.envObject = JSON.parse(localStorage.getItem("envObject") || "{}");
    this.axios = axios.create({
      baseURL: baseUrl || process.env.REACT_APP_DEFAULT_API_SERVER || this.envObject.api_server,
    });
    this.scopes = ["openid"];
    this.useAccessToken = !!useAccessToken;
    this.msalInstance = PublicClientApplicationHolder.getInstance();
  }

  private readonly axios: AxiosInstance;

  private readonly msalInstance: IPublicClientApplication | null;

  private readonly envObject: Record<string, string>;

  private readonly scopes: string[];

  private readonly useAccessToken: boolean;

  public getBaseURL() {
    return this.axios.defaults.baseURL;
  }

  public get(path: string, additionalConfig?: AxiosRequestConfig): Promise<AxiosResponse> {
    return this.getConfig()
      .then((res) => {
        const config = additionalConfig || {};
        config.headers = {
          ...config.headers,
          ...res.headers,
        };
        return this.axios.get(path, config);
      })
      .catch((error) => this.handleAuthenticationError(error));
  }

  public delete(path: string, data?: unknown, additionalConfig?: AxiosRequestConfig): Promise<AxiosResponse> {
    return this.getConfig()
      .then((res) => {
        const config = additionalConfig || {};
        config.headers = {
          ...config.headers,
          ...res.headers,
        };
        return this.axios.delete(path, {
          ...config,
          data,
        });
      })
      .catch((error) => this.handleAuthenticationError(error));
  }

  public deleteByPayload(path: string, payload: unknown): Promise<AxiosResponse> {
    return this.getConfig()
      .then((res) => {
        return this.axios.delete(path, {
          data: payload,
          ...res,
        });
      })
      .catch((error) => this.handleAuthenticationError(error));
  }

  public patch(
    path: string,
    data: unknown,
    additionalConfig: AxiosRequestConfig = {
      headers: {
        "Content-Type": "application/json",
      },
    },
  ): Promise<AxiosResponse> {
    return this.getConfig()
      .then((res) => {
        const config = additionalConfig || {};
        config.headers = {
          ...config.headers,
          ...res.headers,
        };
        return this.axios.patch(path, data, config);
      })
      .catch((error) => this.handleAuthenticationError(error));
  }

  public put(path: string, data?: unknown, additionalConfig?: AxiosRequestConfig): Promise<AxiosResponse> {
    return this.getConfig()
      .then((res) => {
        const config = additionalConfig || {};
        config.headers = {
          ...config.headers,
          ...res.headers,
        };
        return this.axios.put(path, data, config);
      })
      .catch((error) => this.handleAuthenticationError(error));
  }

  public post(path: string, data: unknown, additionalConfig?: AxiosRequestConfig): Promise<AxiosResponse> {
    return this.getConfig()
      .then((res) => {
        const config = additionalConfig || {};
        config.headers = {
          ...config.headers,
          ...res.headers,
        };
        return this.axios.post(path, data, config);
      })
      .catch((error) => this.handleAuthenticationError(error));
  }

  /**
   * Can be used for local development.
   *
   * @param baseUrl
   */
  public withBaseUrl(baseUrl?: string): Api {
    return new Api(this.dialogContext, { baseUrl });
  }

  private acquireAuthTokens = (): Promise<AuthTokens> => {
    if (!this.msalInstance) {
      return Promise.reject(new AuthError("MSAL instance unavailable"));
    }

    const activeAccount = this.msalInstance.getActiveAccount();
    const accounts = this.msalInstance.getAllAccounts();
    const request = {
      scopes: this.scopes,
      account: activeAccount || accounts[0],
    };

    if (!activeAccount && accounts.length === 0) {
      return Promise.reject(new AuthError("User is not signed in."));
    }

    return this.msalInstance
      .acquireTokenSilent(request)
      .then((authResult) => {
        /**
         * The expiration time of the ID token is some times before the expiration time of the access token. It seems
         * like MSAL uses always the expiration time of the access token. So we have to check it manually and force a
         * refresh, if necessary.
         */
        const { exp: idTokenExpiration } = authResult.idTokenClaims as {
          exp: number;
        };
        const now = new Date().getTime() / 1000;
        const expired = idTokenExpiration <= now;
        if (expired) {
          console.warn("ID token expired! Force refresh...");
          return this.msalInstance!.acquireTokenSilent({
            ...request,
            forceRefresh: true,
          });
        }
        return authResult;
      })
      .then((authResult) => ({
        idToken: authResult.idToken,
        accessToken: authResult.accessToken,
      }))
      .catch((error) => {
        console.error(error);
        throw new AuthError("Your session expired. Could not load data.");
      });
  };

  private getConfig = (): Promise<{
    headers: { Authorization?: string };
  }> => {
    return this.acquireAuthTokens().then((tokens) => {
      const { idToken, accessToken } = tokens || {};
      const token = this.useAccessToken ? accessToken : idToken;
      return {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      };
    });
  };

  private handleAuthenticationError(error: Error): never | Promise<never> {
    if (error.name === "AuthError") {
      return this.dialogContext
        .showConfirmationDialog({
          headline: "Your session expired",
          text: "Please note unsaved changes and refresh the page.",
          additionalAction: {
            id: "REFRESH",
            text: "Refresh",
            textColor: tesaDarkRedError,
          },
        })
        .then((id) => {
          if (id === "REFRESH") {
            window.location.reload();
          }
          throw error;
        })
        .catch(() => {
          throw error;
        });
    }
    throw error;
  }
}

export default Api;
